"use client"

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
    fetchEmployerPublicSubscriptionAsync,
    selectEmployerPublicSubscription,
} from "./EmployerPublicSubscriptionSlice"
import { Skeleton, SkeletonText, useDisclosure } from "@chakra-ui/react"
// import Link from "next/link"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import {
    CheckIcon,
    Bars3Icon,
    BoltIcon,
    ClockIcon,
    UserGroupIcon,
    DocumentTextIcon,
    BriefcaseIcon,
    EnvelopeIcon,
    UserIcon,
    XMarkIcon,
    SparklesIcon,
    ShieldCheckIcon,
    RocketLaunchIcon,
} from "@heroicons/react/24/outline"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { Footer } from "../../features/footer/footer"

const MotionDiv = motion.div

const EmployerPublicSubscription = () => {
    const dispatch = useDispatch()
    const subscriptions = useSelector(selectEmployerPublicSubscription)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedTab, setSelectedTab] = useState(0)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [scrolled, setScrolled] = useState(false)

    // Tab mapping as provided
    const tabMapping = {
        "Jobs + Database": "Premium",
        "Unlock Database": "Standard",
        "Job Postings": "Basic",
    }

    // Navigation links
    const navLinks = [
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/pricing" },
        { name: "Blog", path: "/blog" },
        { name: "Job Seeker", path: "https://indiahirings.com" },
    ]

    // Handle scroll event for navbar
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 10) {
                setScrolled(true)
            } else {
                setScrolled(false)
            }
        }

        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    // Reverse mapping for organizing subscriptions by tab
    const reverseTabMapping = Object.entries(tabMapping).reduce((acc, [key, value]) => {
        acc[value] = key
        return acc
    }, {})

    useEffect(() => {
        dispatch(fetchEmployerPublicSubscriptionAsync())
            .then(() => {
                // Simulate a slight delay to show skeleton (remove in production)
                setTimeout(() => setIsLoading(false), 1000)
            })
            .catch(() => setIsLoading(false))
    }, [dispatch])

    // Function to get feature icon based on feature name
    const getFeatureIcon = (featureName) => {
        const iconMap = {
            profile_creation: UserIcon,
            no_of_job_post: BriefcaseIcon,
            no_of_urgent_hiring_tag: BoltIcon,
            no_of_candidate_unlock: UserGroupIcon,
            no_of_job_published: DocumentTextIcon,
            notification_via_email_sms_whatsapp: EnvelopeIcon,
            dedicated_relationship_manager: UserIcon,
            campion_via_email_sms_whatsapp: EnvelopeIcon,
            multiple_user_login: UserGroupIcon,
            reports: DocumentTextIcon,
            company_branding: BriefcaseIcon,
            validity: ClockIcon,
            job_boosts: BoltIcon,
            smart_boost_via_email_sms_whatsapp: EnvelopeIcon,
        }

        return iconMap[featureName] || CheckIcon
    }

    // Function to format feature name for display
    const formatFeatureName = (name) => {
        return name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    // Function to get subscription features
    const getSubscriptionFeatures = (subscription) => {
        const features = []

        // Add all numeric features
        Object.entries(subscription).forEach(([key, value]) => {
            if (
                typeof value === "number" &&
                key !== "mrp_amount" &&
                key !== "amount" &&
                key !== "is_default" &&
                key !== "sort_order" &&
                key !== "validity"
            ) {
                if (value > 0) {
                    let featureText = formatFeatureName(key)

                    // Add quantity for numeric values greater than 1
                    if (value > 1) {
                        featureText = `${featureText}: ${value}`
                    }

                    features.push({
                        name: featureText,
                        value: value,
                        key: key,
                    })
                }
            }
        })

        // Add validity as a feature
        features.push({
            name: `${subscription.validity} ${subscription.validity_type}${subscription.validity > 1 ? "s" : ""} validity`,
            value: subscription.validity,
            key: "validity",
        })

        return features
    }

    // Group subscriptions by type for tabs
    const getSubscriptionsByType = () => {
        const grouped = {}

        Object.keys(reverseTabMapping).forEach((type) => {
            grouped[type] = subscriptions.filter((sub) => sub.subscription_type === type)
        })

        return grouped
    }

    // Calculate savings percentage
    const calculateSavings = (mrp, amount) => {
        return Math.round(((mrp - amount) / mrp) * 100)
    }

    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    }

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    }

    const itemVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    }

    // Render skeleton loaders
    const renderSkeletons = () => {
        return Array(3)
            .fill(0)
            .map((_, index) => (
                <div
                    key={index}
                    className="w-full md:w-[340px] rounded-xl overflow-hidden shadow-lg bg-white border border-gray-100 p-6 relative"
                    style={{ boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)" }}
                >
                    <Skeleton height="30px" width="120px" mb={4} />
                    <SkeletonText noOfLines={1} width="80px" mb={6} />
                    <div className="h-px w-full bg-gray-100 my-6"></div>
                    <div className="space-y-4">
                        {Array(5)
                            .fill(0)
                            .map((_, i) => (
                                <div key={i} className="flex items-center">
                                    <Skeleton height="16px" width="16px" mr={3} borderRadius="full" />
                                    <SkeletonText noOfLines={1} width={`${Math.random() * 40 + 50}%`} />
                                </div>
                            ))}
                    </div>
                    <Skeleton height="40px" mt={6} borderRadius="md" />
                </div>
            ))
    }

    const subscriptionsByType = isLoading ? {} : getSubscriptionsByType()

    // Get the appropriate badge text based on is_default
    const getSubscriptionBadge = (subscription) => {
        if (subscription.is_default === 1) {
            return "DEFAULT PLAN"
        } else {
            return "POPULAR"
        }
    }

    // Get the appropriate button text based on is_default
    const getButtonText = (subscription) => {
        if (subscription.is_default === 1) {
            return "Default Plan (Free)"
        } else {
            return "Subscribe Now"
        }
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Background Elements */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                {/* Top right circle */}
                <div
                    style={{
                        position: "absolute",
                        top: "-100px",
                        right: "-100px",
                        width: "400px",
                        height: "400px",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)",
                    }}
                ></div>

                {/* Bottom left circle */}
                <div
                    style={{
                        position: "absolute",
                        bottom: "-100px",
                        left: "-100px",
                        width: "300px",
                        height: "300px",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)",
                    }}
                ></div>

                {/* Decorative elements */}
                <div
                    style={{
                        position: "absolute",
                        top: "20%",
                        left: "5%",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(59, 130, 246, 0.2)",
                    }}
                ></div>
                <div
                    style={{
                        position: "absolute",
                        top: "30%",
                        right: "10%",
                        width: "15px",
                        height: "15px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(59, 130, 246, 0.15)",
                    }}
                ></div>
                <div
                    style={{
                        position: "absolute",
                        bottom: "25%",
                        right: "15%",
                        width: "25px",
                        height: "25px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                    }}
                ></div>

                {/* Wavy pattern */}
                {/* <svg
                    style={{
                        position: "absolute",
                        bottom: "0",
                        left: "0",
                        width: "100%",
                        height: "200px",
                        opacity: 0.1,
                    }}
                    viewBox="0 0 1440 320"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill="#3b82f6"
                        fillOpacity="1"
                        d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,186.7C672,213,768,235,864,224C960,213,1056,171,1152,149.3C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                    ></path>
                </svg> */}
            </div>

            {/* Navigation Bar - Fixed to always be visible with a semi-transparent background */}
            <header
                className="fixed w-full z-50 transition-all duration-300"
                style={{
                    backgroundColor: scrolled ? "rgba(255, 255, 255, 0.95)" : "rgba(30, 64, 175, 0.95)",
                    backdropFilter: "blur(8px)",
                    boxShadow: scrolled ? "0 2px 10px rgba(0, 0, 0, 0.1)" : "none",
                    padding: scrolled ? "12px 0" : "20px 0",
                }}
            >
                <div className="container mx-auto px-4 md:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        {/* Logo Section */}
                        <MotionDiv
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center"
                        >
                            <Link to="/" className="flex items-center">
                                <h1
                                    style={{
                                        fontSize: "1.5rem",
                                        fontWeight: "700",
                                        color: scrolled ? "#1e40af" : "white",
                                        transition: "color 0.3s ease",
                                    }}
                                >
                                    IndiaHirings
                                </h1>
                            </Link>
                        </MotionDiv>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-8">
                            {navLinks.map((item, index) => (
                                <MotionDiv
                                    key={item.name}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 * index }}
                                    whileHover={{ y: -3 }}
                                >
                                    <Link
                                        to={item.path}
                                        style={{
                                            fontWeight: "500",
                                            color: scrolled ? "#1e293b" : "white",
                                            position: "relative",
                                            textDecoration: "none",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <span
                                            style={{
                                                position: "relative",
                                                paddingBottom: "4px",
                                            }}
                                            className="hover:text-blue-500"
                                        >
                                            {item.name}
                                            <span
                                                style={{
                                                    position: "absolute",
                                                    bottom: "-4px",
                                                    left: "0",
                                                    width: "0%",
                                                    height: "2px",
                                                    backgroundColor: scrolled ? "#3b82f6" : "white",
                                                    transition: "width 0.3s ease",
                                                }}
                                                className="hover:w-full"
                                            ></span>
                                        </span>
                                    </Link>
                                </MotionDiv>
                            ))}
                        </div>

                        {/* Login/Register Button */}
                        <div className="flex items-center space-x-4">
                            <MotionDiv
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                                className="hidden md:block"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Link
                                    to="/login"
                                    style={{
                                        padding: "8px 20px",
                                        borderRadius: "9999px",
                                        fontWeight: "500",
                                        transition: "all 0.3s ease",
                                        border: scrolled ? "1px solid #3b82f6" : "1px solid white",
                                        color: scrolled ? "#3b82f6" : "white",
                                        backgroundColor: "transparent",
                                        display: "inline-block",
                                    }}
                                    className="hover:bg-blue-500 hover:text-white hover:border-blue-500 hover:-translate-y-0.5 hover:shadow-md"
                                >
                                    Login / Register
                                </Link>
                            </MotionDiv>

                            {/* Mobile menu button */}
                            <button className="md:hidden flex items-center" aria-label="Open menu" onClick={onOpen}>
                                <Bars3Icon style={{ width: "24px", height: "24px", color: scrolled ? "#1e293b" : "white" }} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Navigation Drawer */}
            <div className="fixed inset-0 z-50 md:hidden" style={{ display: isOpen ? "block" : "none" }}>
                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose}></div>
                <div
                    className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300"
                    style={{ transform: isOpen ? "translateX(0)" : "translateX(100%)" }}
                >
                    <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                        <h2 style={{ fontWeight: "700", color: "#1e40af" }}>IndiaHirings</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <XMarkIcon style={{ width: "20px", height: "20px" }} />
                        </button>
                    </div>
                    <div className="p-5 space-y-4">
                        {navLinks.map((item) => (
                            <Link
                                key={item.name}
                                to={item.path}
                                style={{
                                    display: "block",
                                    padding: "8px 0",
                                    color: "#1e293b",
                                    fontWeight: "500",
                                    transition: "all 0.2s ease",
                                }}
                                className="hover:text-blue-500 hover:translate-x-1"
                                onClick={onClose}
                            >
                                {item.name}
                            </Link>
                        ))}
                        <div className="pt-4 border-t border-gray-100 mt-4">
                            <Link
                                to="/login"
                                style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "10px 0",
                                    textAlign: "center",
                                    backgroundColor: "#3b82f6",
                                    color: "white",
                                    borderRadius: "8px",
                                    fontWeight: "500",
                                    transition: "all 0.3s ease",
                                }}
                                className="hover:bg-blue-600"
                                onClick={onClose}
                            >
                                Login / Register
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="pt-32 md:pt-40 pb-20 relative z-10">
                <div className="container mx-auto px-4 md:px-6 lg:px-8">
                    {/* Header Section with Gradient Background */}
                    <div className="relative mb-16 md:mb-24">
                        <div
                            className="absolute inset-0 -top-32 -z-10 rounded-b-[40px]"
                            style={{
                                background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
                                height: "400px",
                                boxShadow: "0 10px 30px rgba(59, 130, 246, 0.2)",
                            }}
                        >
                            {/* Decorative elements for header */}
                            <div className="absolute inset-0 overflow-hidden">
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "20%",
                                        left: "10%",
                                        width: "100px",
                                        height: "100px",
                                        borderRadius: "50%",
                                        background: "rgba(255, 255, 255, 0.1)",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: "30%",
                                        right: "15%",
                                        width: "150px",
                                        height: "150px",
                                        borderRadius: "50%",
                                        background: "rgba(255, 255, 255, 0.1)",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "60%",
                                        left: "30%",
                                        width: "70px",
                                        height: "70px",
                                        borderRadius: "50%",
                                        background: "rgba(255, 255, 255, 0.05)",
                                    }}
                                ></div>
                                <svg
                                    className="absolute bottom-0 left-0 w-full"
                                    viewBox="0 0 1440 100"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0 50L48 45.7C96 41.3 192 32.7 288 30.8C384 29 480 34 576 41.7C672 49.3 768 59.7 864 58.3C960 57 1056 44 1152 36.7C1248 29.3 1344 27.7 1392 26.8L1440 26V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z"
                                        fill="rgba(255, 255, 255, 0.1)"
                                    />
                                </svg>
                            </div>
                        </div>

                        <MotionDiv initial="hidden" animate="visible" variants={fadeIn} className="text-center">
                            <h1
                                style={{
                                    fontSize: "2.75rem",
                                    fontWeight: "800",
                                    color: "white",
                                    marginBottom: "1rem",
                                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                Plans and Pricing
                            </h1>
                            <p
                                style={{
                                    fontSize: "1.25rem",
                                    color: "rgba(255, 255, 255, 0.9)",
                                    maxWidth: "700px",
                                    margin: "0 auto",
                                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                Select the perfect plan for your hiring needs and scale your recruitment efforts
                            </p>
                        </MotionDiv>
                    </div>

                    {/* Tabs for Plan Types */}
                    <div className="mb-16 text-center">
                        <div
                            style={{
                                display: "inline-flex",
                                backgroundColor: "white",
                                borderRadius: "9999px",
                                padding: "4px",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            {Object.keys(tabMapping).map((tabName, index) => (
                                <button
                                    key={tabName}
                                    onClick={() => setSelectedTab(index)}
                                    style={{
                                        padding: "10px 24px",
                                        borderRadius: "9999px",
                                        fontWeight: "500",
                                        transition: "all 0.3s ease",
                                        backgroundColor: selectedTab === index ? "#3b82f6" : "transparent",
                                        color: selectedTab === index ? "white" : "#64748b",
                                    }}
                                    className="transition-all duration-300 hover:bg-blue-50"
                                >
                                    {tabName}
                                </button>
                            ))}
                        </div>

                        <div className="mt-16">
                            {Object.entries(tabMapping).map(([tabName, subscriptionType], tabIndex) => (
                                <div key={tabName} style={{ display: selectedTab === tabIndex ? "block" : "none" }}>
                                    <MotionDiv
                                        variants={staggerContainer}
                                        initial="hidden"
                                        animate="visible"
                                        className="flex flex-wrap justify-center gap-8"
                                    >
                                        {isLoading ? (
                                            renderSkeletons()
                                        ) : (
                                            <>
                                                {subscriptionsByType[subscriptionType]?.map((subscription, idx) => (
                                                    <MotionDiv
                                                        key={subscription.uuid}
                                                        variants={itemVariant}
                                                        className="w-full md:w-[340px] hover:-translate-y-2 hover:shadow-xl relative overflow-hidden"
                                                        style={{
                                                            backgroundColor: subscription.is_default === 1 ? "#f0f7ff" : "white",
                                                            borderRadius: "16px",
                                                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
                                                            border: subscription.is_default === 1 ? "1px solid #bfdbfe" : "1px solid #f1f5f9",
                                                            padding: "30px",
                                                            transition: "all 0.3s ease",
                                                        }}
                                                    >
                                                        {/* Decorative background elements */}
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "-20px",
                                                                right: "-20px",
                                                                width: "120px",
                                                                height: "120px",
                                                                borderRadius: "50%",
                                                                background:
                                                                    subscription.is_default === 1
                                                                        ? "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)"
                                                                        : "radial-gradient(circle, rgba(241, 245, 249, 0.8) 0%, rgba(241, 245, 249, 0) 70%)",
                                                                zIndex: "0",
                                                            }}
                                                        ></div>
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                bottom: "-30px",
                                                                left: "-30px",
                                                                width: "150px",
                                                                height: "150px",
                                                                borderRadius: "50%",
                                                                background:
                                                                    subscription.is_default === 1
                                                                        ? "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0) 70%)"
                                                                        : "radial-gradient(circle, rgba(241, 245, 249, 0.5) 0%, rgba(241, 245, 249, 0) 70%)",
                                                                zIndex: "0",
                                                            }}
                                                        ></div>

                                                        {/* Badge */}
                                                        <div className="absolute -top-0.5 right-6 z-10">
                                                            <div
                                                                style={{
                                                                    backgroundColor: subscription.is_default === 1 ? "#3b82f6" : "#8b5cf6",
                                                                    color: "white",
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: "700",
                                                                    padding: "6px 12px",
                                                                    borderRadius: "0 0 8px 8px",
                                                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                                                }}
                                                            >
                                                                {subscription.is_default === 1 ? "DEFAULT PLAN" : "POPULAR"}
                                                            </div>
                                                        </div>

                                                        {/* Icon */}
                                                        <div
                                                            style={{
                                                                width: "48px",
                                                                height: "48px",
                                                                borderRadius: "12px",
                                                                backgroundColor: subscription.is_default === 1 ? "#dbeafe" : "#f1f5f9",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                marginBottom: "16px",
                                                                position: "relative",
                                                                zIndex: "1",
                                                            }}
                                                        >
                                                            {idx === 0 ? (
                                                                <RocketLaunchIcon style={{ width: "24px", height: "24px", color: "#3b82f6" }} />
                                                            ) : idx === 1 ? (
                                                                <SparklesIcon style={{ width: "24px", height: "24px", color: "#3b82f6" }} />
                                                            ) : (
                                                                <ShieldCheckIcon style={{ width: "24px", height: "24px", color: "#3b82f6" }} />
                                                            )}
                                                        </div>

                                                        <div className="mb-6 relative z-10">
                                                            <h3
                                                                style={{
                                                                    fontSize: "1.5rem",
                                                                    fontWeight: "700",
                                                                    color: "#1e293b",
                                                                    marginBottom: "4px",
                                                                }}
                                                            >
                                                                {subscription.title}
                                                            </h3>
                                                            <p style={{ fontSize: "0.875rem", color: "#64748b" }}>{subscription.description}</p>
                                                        </div>

                                                        <div className="flex items-baseline mb-6 relative z-10">
                                                            <span
                                                                style={{
                                                                    fontSize: "2.25rem",
                                                                    fontWeight: "800",
                                                                    color: "#1e293b",
                                                                    background: "linear-gradient(90deg, #1e40af, #3b82f6)",
                                                                    WebkitBackgroundClip: "text",
                                                                    WebkitTextFillColor: "transparent",
                                                                }}
                                                            >
                                                                ₹{subscription.amount}
                                                            </span>
                                                            <div className="ml-2 flex flex-col items-start">
                                                                <span
                                                                    style={{ fontSize: "0.875rem", color: "#64748b", textDecoration: "line-through" }}
                                                                >
                                                                    ₹{subscription.mrp_amount}
                                                                </span>
                                                                <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                                                    / {subscription.validity} {subscription.validity_type}
                                                                    {subscription.validity > 1 ? "s" : ""}
                                                                </span>
                                                            </div>
                                                            {subscription.mrp_amount > subscription.amount && (
                                                                <span
                                                                    className="ml-auto"
                                                                    style={{
                                                                        backgroundColor: "#dcfce7",
                                                                        color: "#16a34a",
                                                                        borderRadius: "9999px",
                                                                        padding: "4px 8px",
                                                                        fontSize: "0.75rem",
                                                                        fontWeight: "600",
                                                                    }}
                                                                >
                                                                    Save {calculateSavings(subscription.mrp_amount, subscription.amount)}%
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div
                                                            style={{
                                                                height: "1px",
                                                                width: "100%",
                                                                background: "linear-gradient(to right, transparent, #e2e8f0, transparent)",
                                                                margin: "24px 0",
                                                            }}
                                                        ></div>

                                                        <ul className="space-y-4 mb-8 relative z-10">
                                                            {getSubscriptionFeatures(subscription).map((feature) => {
                                                                const Icon = getFeatureIcon(feature.key)
                                                                return (
                                                                    <li key={feature.key} className="flex text-left items-start">
                                                                        <div
                                                                            style={{
                                                                                width: "22px",
                                                                                height: "22px",
                                                                                borderRadius: "50%",
                                                                                backgroundColor: "#dbeafe",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                marginRight: "12px",
                                                                                flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            <CheckCircleIcon style={{ width: "14px", height: "14px", color: "#3b82f6" }} />
                                                                        </div>
                                                                        <span style={{ fontSize: "0.875rem", color: "#334155" }}>{feature.name}</span>
                                                                    </li>
                                                                )
                                                            })}
                                                        </ul>

                                                        <button
                                                            style={{
                                                                width: "100%",
                                                                padding: "12px 0",
                                                                borderRadius: "8px",
                                                                fontWeight: "600",
                                                                transition: "all 0.3s ease",
                                                                position: "relative",
                                                                zIndex: "1",
                                                                ...(subscription.is_default === 1
                                                                    ? {
                                                                        backgroundColor: "#3b82f6",
                                                                        color: "white",
                                                                        border: "none",
                                                                    }
                                                                    : {
                                                                        backgroundColor: "white",
                                                                        color: "#3b82f6",
                                                                        border: "1px solid #3b82f6",
                                                                    }),
                                                            }}
                                                            className="hover:shadow-md hover:-translate-y-0.5"
                                                        >
                                                            {subscription.is_default === 1 ? "Default Plan (Free)" : "Subscribe Now"}
                                                        </button>
                                                    </MotionDiv>
                                                ))}

                                                {(!subscriptionsByType[subscriptionType] ||
                                                    subscriptionsByType[subscriptionType].length === 0) && (
                                                        <div className="text-center p-8 w-full">
                                                            <p style={{ color: "#64748b" }}>No {subscriptionType} plans available at the moment.</p>
                                                        </div>
                                                    )}
                                            </>
                                        )}
                                    </MotionDiv>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Plan Comparison */}
                    {/* <div className="mt-24 max-w-5xl mx-auto">
                        <h2
                            style={{
                                fontSize: "2rem",
                                fontWeight: "700",
                                textAlign: "center",
                                marginBottom: "2rem",
                                color: "#1e293b",
                                position: "relative",
                                display: "inline-block",
                            }}
                            className="mx-auto block"
                        >
                            <span
                                style={{
                                    position: "absolute",
                                    bottom: "-10px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "60px",
                                    height: "4px",
                                    backgroundColor: "#3b82f6",
                                    borderRadius: "2px",
                                }}
                            ></span>
                            Plan Comparison
                        </h2>

                        <div
                            style={{
                                backgroundColor: "white",
                                borderRadius: "16px",
                                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                                overflow: "hidden",
                                marginTop: "2rem",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#f8fafc",
                                    padding: "16px 24px",
                                    borderBottom: "1px solid #e2e8f0",
                                }}
                            >
                                <div className="grid grid-cols-4 gap-4">
                                    <div style={{ fontWeight: "600", color: "#1e293b" }}>Features</div>
                                    <div style={{ fontWeight: "600", color: "#1e293b", textAlign: "center" }}>Basic</div>
                                    <div style={{ fontWeight: "600", color: "#1e293b", textAlign: "center" }}>Standard</div>
                                    <div style={{ fontWeight: "600", color: "#1e293b", textAlign: "center" }}>Premium</div>
                                </div>
                            </div>

                            <div style={{ padding: "16px 0" }}>
                                {[
                                    { name: "Job Postings", basic: "5", standard: "15", premium: "Unlimited" },
                                    { name: "Candidate Database Access", basic: "Limited", standard: "Full", premium: "Full" },
                                    { name: "Urgent Hiring Tags", basic: "1", standard: "3", premium: "10" },
                                    { name: "Email Notifications", basic: "✓", standard: "✓", premium: "✓" },
                                    { name: "SMS Notifications", basic: "✗", standard: "✓", premium: "✓" },
                                    { name: "WhatsApp Notifications", basic: "✗", standard: "✗", premium: "✓" },
                                    { name: "Dedicated Manager", basic: "✗", standard: "✗", premium: "✓" },
                                ].map((feature, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            padding: "12px 24px",
                                            borderBottom: index !== 6 ? "1px solid #f1f5f9" : "none",
                                        }}
                                    >
                                        <div className="grid grid-cols-4 gap-4 items-center">
                                            <div style={{ color: "#334155", fontWeight: "500" }}>{feature.name}</div>
                                            <div
                                                style={{
                                                    color: feature.basic === "✗" ? "#94a3b8" : "#334155",
                                                    textAlign: "center",
                                                    fontWeight: feature.basic === "✓" ? "600" : "normal",
                                                }}
                                            >
                                                {feature.basic}
                                            </div>
                                            <div
                                                style={{
                                                    color: feature.standard === "✗" ? "#94a3b8" : "#334155",
                                                    textAlign: "center",
                                                    fontWeight: feature.standard === "✓" ? "600" : "normal",
                                                }}
                                            >
                                                {feature.standard}
                                            </div>
                                            <div
                                                style={{
                                                    color: feature.premium === "✗" ? "#94a3b8" : "#334155",
                                                    textAlign: "center",
                                                    fontWeight: feature.premium === "✓" ? "600" : "normal",
                                                }}
                                            >
                                                {feature.premium}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div> */}

                    {/* Additional Information */}
                    <MotionDiv
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                        className="text-center mt-24 max-w-3xl mx-auto"
                        style={{
                            backgroundColor: "white",
                            borderRadius: "16px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                            padding: "40px",
                            border: "1px solid #f1f5f9",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {/* Decorative elements */}
                        <div
                            style={{
                                position: "absolute",
                                top: "0",
                                right: "0",
                                width: "150px",
                                height: "150px",
                                borderRadius: "0 0 0 100%",
                                background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)",
                            }}
                        ></div>
                        <div
                            style={{
                                position: "absolute",
                                bottom: "0",
                                left: "0",
                                width: "120px",
                                height: "120px",
                                borderRadius: "0 100% 0 0",
                                background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0) 70%)",
                            }}
                        ></div>

                        <div
                            style={{
                                width: "64px",
                                height: "64px",
                                borderRadius: "16px",
                                backgroundColor: "#eff6ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 20px",
                            }}
                        >
                            <SparklesIcon style={{ width: "32px", height: "32px", color: "#3b82f6" }} />
                        </div>

                        <h3
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "700",
                                color: "#1e293b",
                                marginBottom: "1rem",
                                position: "relative",
                                zIndex: "1",
                            }}
                        >
                            Need a custom plan?
                        </h3>
                        <p
                            style={{
                                color: "#64748b",
                                marginBottom: "1.5rem",
                                position: "relative",
                                zIndex: "1",
                            }}
                        >
                            Contact our sales team for a tailored solution that meets your specific requirements and scales with your
                            business needs
                        </p>
                        <button
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "12px 24px",
                                borderRadius: "9999px",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                fontWeight: "600",
                                border: "none",
                                transition: "all 0.3s ease",
                                position: "relative",
                                zIndex: "1",
                            }}
                            className="hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-md"
                        >
                            <span>Contact Sales</span>
                            <EnvelopeIcon style={{ width: "20px", height: "20px", marginLeft: "8px" }} />
                        </button>
                    </MotionDiv>

                    {/* Features Grid */}
                    <div className="mt-24">
                        <h2
                            style={{
                                fontSize: "2rem",
                                fontWeight: "700",
                                textAlign: "center",
                                marginBottom: "1rem",
                                color: "#1e293b",
                            }}
                        >
                            Why Choose IndiaHirings?
                        </h2>
                        <p
                            style={{
                                textAlign: "center",
                                color: "#64748b",
                                maxWidth: "700px",
                                margin: "0 auto 3rem",
                            }}
                        >
                            Streamline your recruitment process with our comprehensive hiring platform
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                {
                                    icon: BriefcaseIcon,
                                    title: "Job Postings",
                                    description: "Post jobs and reach thousands of qualified candidates instantly",
                                },
                                {
                                    icon: UserGroupIcon,
                                    title: "Candidate Database",
                                    description: "Access our extensive database of pre-screened candidates",
                                },
                                {
                                    icon: BoltIcon,
                                    title: "Quick Hiring",
                                    description: "Accelerate your hiring process with our urgent hiring features",
                                },
                                {
                                    icon: DocumentTextIcon,
                                    title: "Detailed Reports",
                                    description: "Get insights into your recruitment process with detailed analytics",
                                },
                                {
                                    icon: UserIcon,
                                    title: "Dedicated Support",
                                    description: "Get personalized assistance from our relationship managers",
                                },
                                {
                                    icon: EnvelopeIcon,
                                    title: "Multi-channel Notifications",
                                    description: "Reach candidates via email, SMS, and WhatsApp",
                                },
                            ].map((feature, index) => (
                                <MotionDiv
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        backgroundColor: "white",
                                        borderRadius: "16px",
                                        padding: "30px",
                                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                                        border: "1px solid #f1f5f9",
                                        transition: "all 0.3s ease",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                    className="hover:shadow-lg hover:-translate-y-1"
                                >
                                    {/* Decorative background */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "0",
                                            right: "0",
                                            width: "100px",
                                            height: "100px",
                                            borderRadius: "0 0 0 100%",
                                            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0) 70%)",
                                        }}
                                    ></div>

                                    <div
                                        style={{
                                            width: "56px",
                                            height: "56px",
                                            borderRadius: "16px",
                                            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginBottom: "20px",
                                            position: "relative",
                                        }}
                                    >
                                        <feature.icon style={{ width: "28px", height: "28px", color: "#3b82f6" }} />
                                    </div>
                                    <h3
                                        style={{
                                            fontSize: "1.25rem",
                                            fontWeight: "700",
                                            color: "#1e293b",
                                            marginBottom: "10px",
                                            position: "relative",
                                        }}
                                    >
                                        {feature.title}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: "0.875rem",
                                            color: "#64748b",
                                            position: "relative",
                                        }}
                                    >
                                        {feature.description}
                                    </p>
                                </MotionDiv>
                            ))}
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="mt-24 max-w-3xl mx-auto">
                        <h2
                            style={{
                                fontSize: "2rem",
                                fontWeight: "700",
                                textAlign: "center",
                                marginBottom: "1rem",
                                color: "#1e293b",
                            }}
                        >
                            Frequently Asked Questions
                        </h2>
                        <p
                            style={{
                                textAlign: "center",
                                color: "#64748b",
                                maxWidth: "600px",
                                margin: "0 auto 3rem",
                            }}
                        >
                            Find answers to common questions about our subscription plans
                        </p>

                        <div className="space-y-4">
                            {[
                                {
                                    question: "What is the default plan?",
                                    answer:
                                        "The default plan is automatically assigned to all new users upon registration. It provides basic features to help you get started with our platform at no cost.",
                                },
                                {
                                    question: "How do I upgrade my subscription?",
                                    answer:
                                        "You can upgrade your subscription at any time from your account dashboard. Simply navigate to the 'Subscription' section and choose the plan that best fits your needs.",
                                },
                                {
                                    question: "Can I downgrade my plan?",
                                    answer:
                                        "Yes, you can downgrade your plan at the end of your current billing cycle. Your current features will remain active until the end of your paid period.",
                                },
                                {
                                    question: "Do you offer refunds?",
                                    answer:
                                        "We offer a 7-day money-back guarantee if you're not satisfied with our services. Please contact our support team for assistance with refunds.",
                                },
                            ].map((faq, index) => (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: "white",
                                        borderRadius: "12px",
                                        padding: "24px",
                                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                                        border: "1px solid #f1f5f9",
                                    }}
                                >
                                    <h3
                                        style={{
                                            fontSize: "1.125rem",
                                            fontWeight: "600",
                                            color: "#1e293b",
                                            marginBottom: "12px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: "24px",
                                                height: "24px",
                                                borderRadius: "50%",
                                                backgroundColor: "#eff6ff",
                                                color: "#3b82f6",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginRight: "12px",
                                                fontSize: "0.875rem",
                                                fontWeight: "700",
                                            }}
                                        >
                                            Q
                                        </span>
                                        {faq.question}
                                    </h3>
                                    <p
                                        style={{
                                            color: "#64748b",
                                            paddingLeft: "36px",
                                        }}
                                    >
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}

            <Footer />
        </div>
    )
}

export default EmployerPublicSubscription
